'use strict';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import DomElementBuilder from '../../sxQuery/DomElementBuilder';
/**
 * Defaults applied per field when the avatar's `simpleAvatarConfig` doesn't set
 * a value (same pattern as the plugin's DefaultConfig). No default `imageUrl` —
 * a built-in accent-coloured mark is generated at runtime instead.
 */
const DefaultSimpleAvatarConfig = {
    shape: 'circle',
    motionPreset: 'bubble',
    intensity: 0.7
};
/** The plugin accent custom property (set on `body` from `style.accentColor`). */
const ACCENT_CSS_VAR = '--ss360-accent-color';
/** Matches the `--ss360-accent-color` default in `abstracts/_variables.scss`. */
const FALLBACK_ACCENT = '#3d8fff';
/** Resolve the plugin accent colour as a literal value (for the inline default SVG). */
function resolveAccentColor(el) {
    const val = window.getComputedStyle(el).getPropertyValue(ACCENT_CSS_VAR);
    return (val && val.trim()) || FALLBACK_ACCENT;
}
const VALID_SHAPES = ['circle', 'rounded', 'square'];
// 'none' = static image without decorations/motion (states still tracked and
// exposed via modifier classes / data-state / the avatarStateChange callback).
const VALID_PRESETS = ['bubble', 'ring', 'halo', 'none'];
/** BEM block the renderer builds; states/preset/shape are `--` modifiers on it. */
const BLOCK = 'ss360-avatar__simple';
/** Level above which playback audio counts as the avatar "talking". */
const TALKING_LEVEL_THRESHOLD = 0.04;
/** How long (ms) the talking state survives a dip below the threshold. */
const TALKING_HOLD_MS = 600;
/**
 * How long (ms) the thinking state may wait for reply audio before decaying to
 * idle — a VAD false-positive produces no reply, and without a decay the
 * avatar would "think" forever.
 */
const THINKING_TIMEOUT_MS = 10000;
/** Built-in default image: a minimal speech-bubble mark in the accent colour. */
function defaultImageDataUri(accentColor) {
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96">'
        + `<circle cx="48" cy="48" r="46" fill="${accentColor}"/>`
        + '<path fill="#fff" d="M48 26c-13.3 0-24 8.5-24 19 0 6.1 3.7 11.6 9.4 15.1L31 70.5c-.4 1.1.7 2.1 1.7 1.5l10.6-6.1c1.5.3 3.1.4 4.7.4 13.3 0 24-8.5 24-19S61.3 26 48 26z"/>'
        + `<circle cx="38" cy="45" r="3.4" fill="${accentColor}"/>`
        + `<circle cx="48" cy="45" r="3.4" fill="${accentColor}"/>`
        + `<circle cx="58" cy="45" r="3.4" fill="${accentColor}"/>`
        + '</svg>';
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
/** Merge the avatar config with the defaults and validate the values. */
function resolveConfig(config) {
    const own = (key) => {
        const val = config ? config[key] : undefined;
        return val !== undefined && val !== null && val !== '' ? val : undefined;
    };
    const shape = own('shape');
    const motionPreset = own('motionPreset');
    const rawIntensity = own('intensity');
    const intensity = typeof rawIntensity === 'number' && !isNaN(rawIntensity)
        ? Math.min(1, Math.max(0, rawIntensity))
        : DefaultSimpleAvatarConfig.intensity;
    return {
        imageUrl: own('imageUrl') || null,
        shape: (shape && VALID_SHAPES.indexOf(shape) !== -1 ? shape : DefaultSimpleAvatarConfig.shape),
        motionPreset: (motionPreset && VALID_PRESETS.indexOf(motionPreset) !== -1 ? motionPreset : DefaultSimpleAvatarConfig.motionPreset),
        intensity
    };
}
export default class SimpleAvatarRenderer {
    /**
     * @param ctx renderer context (container, asset base, audio accessors)
     * @param config the avatar's `simpleAvatarConfig` (may be null/partial)
     */
    constructor(ctx, config) {
        this.wrapper = null;
        this.analyser = null;
        this.analyserData = null;
        /** The gain node the analyser was connected to (for a reliable disconnect). */
        this.tappedGainNode = null;
        this.rafId = null;
        this.mounted = false;
        // State machine inputs (see updateState()).
        this.state = 'idle';
        this.listening = false;
        /** Listening just ended and no reply audio has started yet → thinking. */
        this.awaitingResponse = false;
        /** When the thinking wait started (performance.now()). */
        this.awaitingResponseSince = 0;
        /** Decay fallback for the no-amplitude-loop path (audio never attached). */
        this.thinkingDecayTimer = null;
        this.lastLoudAt = 0;
        this.smoothedLevel = 0;
        this.lastAppliedLevel = -1;
        this.ctx = ctx;
        this.config = config;
        this.reducedMotion = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    mount() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.mounted)
                return;
            try {
                const resolved = resolveConfig(this.config);
                this.buildDom(resolved);
                this.mounted = true;
                // Visually ready as soon as the DOM is in place — the decorations
                // animate at once and the image fades in when it loads.
                if (this.ctx.onLoaded)
                    this.ctx.onLoaded();
            }
            catch (e) {
                console.error('[avatar] Simple avatar setup failed:', e);
                if (this.ctx.onError)
                    this.ctx.onError({ message: e && e.message ? e.message : 'Avatar failed to load' });
            }
        });
    }
    buildDom(resolved) {
        const container = this.ctx.container;
        container.innerHTML = '';
        // The decorations use $avatar-accent directly in the CSS; only the
        // built-in default SVG needs the accent as a literal value.
        const accentColor = resolveAccentColor(container);
        const img = DomElementBuilder.createElement({
            tag: 'img',
            classList: [`${BLOCK}-img`],
            attrs: { alt: '', decoding: 'async' }
        });
        img.addEventListener('load', () => img.classList.add(`${BLOCK}-img--loaded`));
        img.addEventListener('error', () => {
            // Broken imageUrl (bad config entry / dead link) → built-in default.
            const fallback = defaultImageDataUri(accentColor);
            if (img.src !== fallback) {
                console.warn('[avatar] Simple avatar image failed to load, using built-in default:', resolved.imageUrl);
                img.src = fallback;
            }
        });
        // src is assigned after the listeners are wired so no event is missed.
        img.src = resolved.imageUrl || defaultImageDataUri(accentColor);
        // Decorations (rings + halo) sit behind the image; which ones show —
        // and how they respond to --level — is decided per preset/state in CSS.
        const figure = DomElementBuilder.createElement({
            tag: 'div',
            classList: [`${BLOCK}-figure`],
            children: [
                DomElementBuilder.createElement({ tag: 'div', classList: [`${BLOCK}-ring`, `${BLOCK}-ring--1`] }),
                DomElementBuilder.createElement({ tag: 'div', classList: [`${BLOCK}-ring`, `${BLOCK}-ring--2`] }),
                DomElementBuilder.createElement({ tag: 'div', classList: [`${BLOCK}-halo`] }),
                img
            ]
        });
        // The `data-state` attribute is a non-styling hook for custom callbacks;
        // the shipped CSS styles via the BEM modifier classes only.
        const wrapper = DomElementBuilder.createElement({
            tag: 'div',
            classList: [
                BLOCK,
                `${BLOCK}--${this.state}`,
                `${BLOCK}--preset-${resolved.motionPreset}`,
                `${BLOCK}--shape-${resolved.shape}`
            ],
            data: { state: this.state },
            children: [figure]
        });
        // Custom properties can't go through the builder's style map (it only
        // handles camelCase style properties).
        wrapper.style.setProperty('--intensity', String(resolved.intensity));
        wrapper.style.setProperty('--level', '0');
        container.appendChild(wrapper);
        this.wrapper = wrapper;
    }
    // ── Audio → amplitude ─────────────────────────────────────────────
    attachAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            const audioContext = this.ctx.getAudioContext();
            const gainNode = this.ctx.getPlaybackGainNode();
            if (!audioContext || !gainNode)
                return; // prerequisites not ready — a later call retries
            if (this.analyser)
                return; // already attached
            // A cheap analysis tap: gain → analyser (playback keeps flowing to the
            // destination through the gain node's existing connection).
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 512;
            analyser.smoothingTimeConstant = 0.5;
            gainNode.connect(analyser);
            this.tappedGainNode = gainNode;
            this.analyser = analyser;
            this.analyserData = new Uint8Array(analyser.fftSize);
            // The loop always runs so the state machine (talking detection) stays
            // accurate; with reduced motion updateLevel skips publishing --level,
            // so the CSS never receives voice-driven transforms.
            this.startTicking();
        });
    }
    startTicking() {
        if (this.rafId !== null)
            return;
        const tick = () => {
            this.rafId = requestAnimationFrame(tick);
            this.updateLevel();
            this.updateState();
        };
        this.rafId = requestAnimationFrame(tick);
    }
    /** Read the time-domain amplitude and publish it as the `--level` CSS var. */
    updateLevel() {
        if (!this.analyser || !this.analyserData || !this.wrapper)
            return;
        this.analyser.getByteTimeDomainData(this.analyserData);
        let sumSquares = 0;
        for (let i = 0; i < this.analyserData.length; i++) {
            const v = (this.analyserData[i] - 128) / 128;
            sumSquares += v * v;
        }
        const rms = Math.sqrt(sumSquares / this.analyserData.length);
        // Typical speech RMS peaks well below 1 — rescale so the motion range is used.
        const level = Math.min(1, rms * 3.5);
        // Fast attack, slow release — pulses hit with the voice, decay smoothly.
        this.smoothedLevel = level > this.smoothedLevel
            ? this.smoothedLevel * 0.4 + level * 0.6
            : this.smoothedLevel * 0.88 + level * 0.12;
        if (this.smoothedLevel > TALKING_LEVEL_THRESHOLD) {
            this.lastLoudAt = performance.now();
        }
        const rounded = Math.round(this.smoothedLevel * 200) / 200;
        // The amplitude is always measured (talking detection above), but with
        // reduced motion it must not reach the CSS.
        if (!this.reducedMotion && rounded !== this.lastAppliedLevel) {
            this.lastAppliedLevel = rounded;
            this.wrapper.style.setProperty('--level', String(rounded));
        }
    }
    /** Derive the visual state: listening > talking > thinking > idle. */
    updateState() {
        let next;
        if (this.listening) {
            next = 'listening';
        }
        else if (this.lastLoudAt && performance.now() - this.lastLoudAt < TALKING_HOLD_MS) {
            next = 'talking';
            this.awaitingResponse = false;
        }
        else if (this.awaitingResponse && performance.now() - this.awaitingResponseSince < THINKING_TIMEOUT_MS) {
            next = 'thinking';
        }
        else {
            // No reply audio within the timeout (e.g. a VAD false-positive) —
            // stop waiting instead of thinking forever.
            this.awaitingResponse = false;
            next = 'idle';
        }
        this.applyState(next);
    }
    applyState(next) {
        if (next === this.state)
            return;
        const prev = this.state;
        this.state = next;
        if (this.wrapper) {
            this.wrapper.classList.remove(`${BLOCK}--${prev}`);
            this.wrapper.classList.add(`${BLOCK}--${next}`);
            this.wrapper.dataset.state = next;
            // The widget forwards this to the global `avatarStateChange` callback.
            if (this.ctx.onStateChange)
                this.ctx.onStateChange(next);
        }
    }
    // ── Widget hooks ──────────────────────────────────────────────────
    /** Server-side mic VAD: user started/stopped speaking. */
    setListening(listening) {
        this.listening = listening;
        // Between the user finishing and the reply audio arriving → thinking.
        this.awaitingResponse = !listening;
        this.clearThinkingDecay();
        if (listening) {
            this.applyState('listening');
        }
        else {
            this.awaitingResponseSince = performance.now();
            if (this.rafId === null) {
                // No amplitude loop (audio not attached) — apply directly and
                // decay via timer instead of updateState().
                this.applyState('thinking');
                this.thinkingDecayTimer = window.setTimeout(() => {
                    this.thinkingDecayTimer = null;
                    if (this.state === 'thinking' && this.awaitingResponse) {
                        this.awaitingResponse = false;
                        this.applyState('idle');
                    }
                }, THINKING_TIMEOUT_MS);
            }
        }
    }
    clearThinkingDecay() {
        if (this.thinkingDecayTimer !== null) {
            clearTimeout(this.thinkingDecayTimer);
            this.thinkingDecayTimer = null;
        }
    }
    /** Mood is exposed as `data-mood` (a hook for custom styling/callbacks only). */
    setMood(mood) {
        if (this.wrapper) {
            this.wrapper.dataset.mood = mood;
        }
    }
    destroy() {
        this.clearThinkingDecay();
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this.analyser) {
            // Disconnect from the node the analyser was actually attached to —
            // getPlaybackGainNode() may already return null when the session is
            // torn down before the renderer.
            if (this.tappedGainNode) {
                try {
                    this.tappedGainNode.disconnect(this.analyser);
                }
                catch (e) { /* ignore */ }
            }
            this.analyser = null;
            this.analyserData = null;
        }
        this.tappedGainNode = null;
        if (this.ctx.container) {
            this.ctx.container.innerHTML = '';
        }
        this.wrapper = null;
        this.mounted = false;
    }
}
